"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { List, X, MapPin } from "@phosphor-icons/react";

import {
  accountMenuModel,
  type AccountMenuItem,
  type AccountTier,
  type AccountRole,
} from "@/lib/nav/account-menu";
import { primaryNavLinks, isNavLinkActive } from "@/lib/nav/primary-nav";

interface MobileNavProps {
  tier: AccountTier;
  role: AccountRole;
  displayName: string | null;
  initial: string;
  isProvider?: boolean;
  savedCount?: number;
  pathname: string;
  locationSlug?: string;
  locationName?: string;
}

/**
 * Mobile hamburger menu (#1011) — client component rendered by the server
 * TopBar. Visible only below `md` (<768px). Opens a right-edge slide-out panel
 * with the primary nav links + a tier-aware account section. My Family
 * sub-items are expanded by default (no nested hover on touch).
 */
export function MobileNav({
  tier,
  role,
  displayName,
  initial,
  isProvider,
  savedCount,
  pathname,
  locationSlug,
  locationName,
}: MobileNavProps) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  const model = accountMenuModel({
    tier,
    role,
    displayName,
    savedCount,
    isProvider,
  });
  const navLinks = primaryNavLinks();
  const isSignedIn = tier !== "anonymous";

  // SSR-mount detection: setState in mount effect is the canonical
  // hydration-safe pattern; cascading-render risk is bounded by the
  // [] dep array (runs exactly once on mount).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- see comment above effect
    setMounted(true);
  }, []);

  // Close on route change (pathname prop updates from server).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- see comment above effect
    setOpen(false);
  }, [pathname]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Swipe right to close
  const touchStartX = useRef<number | null>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    // Swipe right by at least 60px to close
    if (deltaX > 60) setOpen(false);
    touchStartX.current = null;
  }, []);

  // Close on Escape key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") setOpen(false);
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [open, handleKeyDown]);

  const tierBadge = model.header.tier === "premium" ? "PREMIUM" : "FREE";

  return (
    <div className="md:hidden">
      {/* Hamburger button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open menu"
        className="relative flex h-11 w-11 items-center justify-center rounded-lg text-wildroost-ink hover:bg-wildroost-teal/10 hover:text-wildroost-teal active:bg-wildroost-teal/10 active:text-wildroost-teal"
      >
        <List size={24} aria-hidden="true" />
        {isSignedIn && model.header.showDot && (
          <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-wildroost-terra" />
        )}
      </button>

      {/* Portal the backdrop + drawer to document.body so they escape
          the header's backdrop-blur stacking context (iOS Safari bug) */}
      {mounted &&
        createPortal(
          <>
            {/* Backdrop */}
            {open && (
              <div
                className="fixed inset-0 z-[300] bg-black/40 transition-opacity"
                onClick={() => setOpen(false)}
                aria-hidden="true"
              />
            )}

            {/* Right-edge slide-out drawer */}
            <div
              ref={drawerRef}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              className={`fixed right-0 top-0 z-[301] flex h-full w-72 max-w-[85vw] flex-col bg-white shadow-xl transition-transform duration-200 ease-in-out ${
                open ? "translate-x-0" : "translate-x-full"
              }`}
              role="dialog"
              aria-modal="true"
              aria-label="Navigation menu"
            >
              {/* Header with close button */}
              <div className="flex items-center justify-between border-b border-wildroost-ink/10 px-4 py-3">
                <div className="flex flex-col">
                  <span className="font-display text-lg font-extrabold tracking-tight text-wildroost-teal">
                    Wildroost
                  </span>
                  {locationName && (
                    <Link
                      href={`/${locationSlug ?? "bend-or"}/camps`}
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-1 text-xs text-wildroost-ink/50 hover:text-wildroost-teal"
                    >
                      <MapPin size={12} aria-hidden="true" />
                      {locationName}
                    </Link>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close menu"
                  className="flex h-11 w-11 items-center justify-center rounded-lg text-wildroost-ink hover:bg-wildroost-teal/10 hover:text-wildroost-teal active:bg-wildroost-teal/10 active:text-wildroost-teal"
                >
                  <X size={24} aria-hidden="true" />
                </button>
              </div>

              {/* Nav items */}
              <nav
                aria-label="Mobile navigation"
                className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-4"
              >
                {/* Primary links — shared with desktop. */}
                {navLinks.map((link) => (
                  <MobileLink
                    key={link.href}
                    href={link.href}
                    active={isNavLinkActive(link.href, pathname)}
                    onClick={() => setOpen(false)}
                  >
                    {link.label}
                  </MobileLink>
                ))}

                <hr className="my-2 border-wildroost-ink/10" />

                {isSignedIn ? (
                  <>
                    {/* Account header: name + tier badge (no email). */}
                    <div className="flex items-center gap-3 px-3 py-2">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-wildroost-teal text-sm font-bold text-white">
                        {initial}
                      </span>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-wildroost-ink">
                          {model.header.name}
                        </div>
                        <span
                          className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                            model.header.tier === "premium"
                              ? "bg-wildroost-gold/15 text-wildroost-gold"
                              : "bg-wildroost-teal/10 text-wildroost-teal"
                          }`}
                        >
                          {tierBadge}
                        </span>
                      </div>
                    </div>

                    {/* Tier-aware sections — sub-items expanded by default. */}
                    {model.sections.map((section) => (
                      <div key={section.id} className="mt-1">
                        {section.href ? (
                          <Link
                            href={section.href}
                            onClick={() => setOpen(false)}
                            className="block px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-wildroost-teal/70"
                          >
                            {section.label}
                          </Link>
                        ) : (
                          <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-wildroost-teal/60">
                            {section.label}
                          </div>
                        )}
                        {section.items.map((item) => (
                          <MobileSubItem
                            key={item.id}
                            item={item}
                            active={pathname === item.href}
                            onClick={() => setOpen(false)}
                          />
                        ))}
                      </div>
                    ))}

                    {/* Upgrade CTA (free) — links to pricing; modal flow lives on
                        finder/detail surfaces (#1011 reuses that pattern). */}
                    {model.upgradeCta && (
                      <Link
                        href="/pricing"
                        onClick={() => setOpen(false)}
                        className="mt-3 block rounded-lg bg-wildroost-gold px-4 py-2.5 text-center text-base font-bold text-white transition-colors hover:bg-[var(--gold-hover)] active:bg-[var(--gold-active)]"
                      >
                        {model.upgradeCta.label}
                      </Link>
                    )}

                    {model.signOut && (
                      <>
                        <hr className="my-2 border-wildroost-ink/10" />
                        <form method="post" action={model.signOut.href}>
                          <button
                            type="submit"
                            className="w-full rounded-lg px-3 py-2.5 text-left text-base font-semibold text-wildroost-ink hover:bg-wildroost-teal/10 hover:text-wildroost-teal active:bg-wildroost-teal/10 active:text-wildroost-teal"
                          >
                            {model.signOut.label}
                          </button>
                        </form>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {model.authActions.map((action, idx) =>
                      idx === 0 ? (
                        <MobileLink
                          key={action.id}
                          href={action.href}
                          active={pathname === action.href}
                          onClick={() => setOpen(false)}
                        >
                          {action.label}
                        </MobileLink>
                      ) : (
                        <Link
                          key={action.id}
                          href={action.href}
                          onClick={() => setOpen(false)}
                          className="mt-2 block rounded-lg bg-wildroost-gold px-4 py-2.5 text-center text-base font-bold text-white transition-colors hover:bg-[var(--gold-hover)] active:bg-[var(--gold-active)]"
                        >
                          {action.label}
                        </Link>
                      ),
                    )}
                  </>
                )}
              </nav>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function MobileLink({
  href,
  active,
  onClick,
  children,
}: {
  href: string;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Link
      href={href}
      aria-current={active ? "page" : undefined}
      onClick={onClick}
      className={`rounded-lg px-3 py-2.5 text-base font-semibold transition-colors ${
        active
          ? "bg-wildroost-teal/10 text-wildroost-teal"
          : "text-wildroost-ink hover:bg-wildroost-teal/10 hover:text-wildroost-teal active:bg-wildroost-teal/10 active:text-wildroost-teal"
      }`}
    >
      {children}
    </Link>
  );
}

function MobileSubItem({
  item,
  active,
  onClick,
}: {
  item: AccountMenuItem;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Link
      href={item.href}
      aria-current={active ? "page" : undefined}
      onClick={onClick}
      className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-base font-medium transition-colors ${
        active
          ? "bg-wildroost-teal/10 text-wildroost-teal"
          : "text-wildroost-ink hover:bg-wildroost-teal/10 hover:text-wildroost-teal active:bg-wildroost-teal/10 active:text-wildroost-teal"
      }`}
    >
      <span className="flex items-center gap-2">
        {item.label}
        {item.showDot && (
          <span className="h-2 w-2 rounded-full bg-wildroost-terra" aria-label="Needs attention" />
        )}
      </span>
      {item.badge && (
        <span className="shrink-0 text-xs font-medium text-wildroost-ink/40">{item.badge}</span>
      )}
    </Link>
  );
}
